import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/lib/i18n";
import AppHeader from "@/components/AppHeader";
import BackButton from "@/components/BackButton";
import { Badge } from "@/components/ui/badge";
import { TYPE_META } from "@/pages/MarketplacePage";
import { labelCat, labelSub, labelItem } from "@/lib/marketplaceLabels";
import { Loader2, MessageCircle, Lock, User as UserIcon, CheckCircle2, Clock, MapPin, AlertTriangle, Leaf } from "lucide-react";

const LISTING_STATE_META = {
  published: { key: "state_published", cls: "bg-emerald-100 text-emerald-800", icon: null },
  pending_confirmation: { key: "state_pending", cls: "bg-amber-100 text-amber-800", icon: Clock },
  closed: { key: "state_closed", cls: "bg-slate-200 text-slate-700", icon: CheckCircle2 },
};

const REPORT_STATE_META = {
  published: { key: "state_published", cls: "bg-emerald-100 text-emerald-800" },
  in_progress: { key: "state_in_progress", cls: "bg-blue-100 text-blue-800" },
  in_review: { key: "state_pending", cls: "bg-yellow-100 text-yellow-800" },
  reopened: { key: "state_active", cls: "bg-orange-100 text-orange-800" },
  resolved: { key: "state_resolved", cls: "bg-emerald-100 text-emerald-800" },
  confirmed: { key: "state_active", cls: "bg-emerald-100 text-emerald-800" },
  pending_moderation: { key: "state_pending", cls: "bg-amber-100 text-amber-800" },
  rejected: { key: "state_rejected", cls: "bg-slate-200 text-slate-700" },
};

const RISK_META = {
  green: { label: "Ordinaria", color: "#059669" },
  yellow: { label: "Da monitorare", color: "#D97706" },
  red: { label: "Urgente", color: "#DC2626" },
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return "—"; }
};

export default function MyListings() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [listings, setListings] = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all"); // all | reports | sale | gift | reusable

  useEffect(() => { if (!authLoading && !user) nav("/auth?mode=signin"); }, [user, authLoading, nav]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [l, r] = await Promise.all([
          api.get("/me/marketplace", { params: { include_closed: true } }).then((x) => x.data).catch(() => []),
          api.get("/me/reports").then((x) => x.data).catch(() => []),
        ]);
        if (!alive) return;
        setListings(l || []);
        setReports(r || []);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  // Normalize to a unified card model for rendering.
  const unified = useMemo(() => {
    const l = listings.map((x) => ({
      kind: "listing",
      id: x.listing_id,
      type: x.type,           // sale | gift | reusable
      title: x.title,
      photos: x.photos,
      state: x.state,
      price: x.price,
      created_at: x.created_at,
      closed_at: x.closed_at,
      category: x.category,
      subcategory: x.subcategory,
      item_type: x.item_type,
      selected_buyer_id: x.selected_buyer_id,
      selected_buyer_name: x.selected_buyer_name,
      province: x.province_name,
      link: `/marketplace/${x.listing_id}`,
    }));
    const r = reports.map((x) => ({
      kind: "report",
      id: x.report_id,
      type: "report",
      title: x.description || x.category || "Segnalazione",
      photos: x.photos,
      state: x.status,
      risk: x.risk_level,
      category: x.category,
      subcategory: x.subcategory,
      created_at: x.created_at,
      province: x.province_name,
      link: `/report/${x.report_id}`,
    }));
    return [...l, ...r].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [listings, reports]);

  const filtered = useMemo(() => {
    if (tab === "all") return unified;
    if (tab === "reports") return unified.filter((x) => x.kind === "report");
    return unified.filter((x) => x.kind === "listing" && x.type === tab);
  }, [unified, tab]);

  const counts = useMemo(() => ({
    all: unified.length,
    reports: unified.filter((x) => x.kind === "report").length,
    sale: unified.filter((x) => x.type === "sale").length,
    gift: unified.filter((x) => x.type === "gift").length,
    reusable: unified.filter((x) => x.type === "reusable").length,
  }), [unified]);

  return (
    <div className="min-h-screen bg-[#F7F5F0]" data-testid="my-listings-page">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 md:px-8 pt-6 pb-4"><BackButton /></div>
      <div className="max-w-5xl mx-auto px-4 md:px-8 pb-10">
        <div className="text-xs uppercase tracking-[0.25em] text-slate-600 font-semibold mb-1 inline-flex items-center gap-1">
          <Lock size={11} /> Solo tu puoi vedere questa pagina
        </div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-1" data-testid="my-listings-heading">I miei annunci</h1>
        <p className="text-sm text-slate-600 mb-5 max-w-2xl">
          In questa sezione trovi tutto ciò che hai pubblicato: le tue segnalazioni ambientali e gli annunci del Marketplace (Vendo, Regalo, Riutilizzabile).
        </p>

        <div className="flex gap-2 flex-wrap mb-4" role="tablist">
          {[
            { k: "all", lb: "Tutti", color: "#0f172a" },
            { k: "reports", lb: "Segnalazioni", color: "#059669" },
            { k: "sale", lb: "Vendo", color: "#7C3AED" },
            { k: "gift", lb: "Regalo", color: "#2563EB" },
            { k: "reusable", lb: "Riutilizzabili trovati", color: "#8B5E34" },
          ].map((t) => (
            <button
              key={t.k}
              data-testid={`ml-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`h-9 px-4 rounded-full text-xs font-semibold border ${tab === t.k ? "text-white" : "bg-white text-slate-700 border-slate-200"}`}
              style={tab === t.k ? { background: t.color, borderColor: t.color } : {}}
            >
              {t.lb} <span className="ml-1 opacity-70">{counts[t.k]}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="p-10 text-slate-500 text-sm flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Caricamento…</div>
        ) : filtered.length === 0 ? (
          <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-10 text-center text-sm text-slate-500 italic" data-testid="ml-empty">
            Nessun elemento in questa vista.
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((x) => (x.kind === "listing"
              ? <ListingRow key={`l-${x.id}`} x={x} />
              : <ReportRow key={`r-${x.id}`} x={x} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ListingRow({ x }) {
  const { t } = useI18n();
  const M = TYPE_META[x.type] || TYPE_META.sale;
  const S = LISTING_STATE_META[x.state] || LISTING_STATE_META.published;
  const Icon = M.icon;
  const isSale = x.type === "sale";
  return (
    <div data-testid={`ml-row-${x.id}`} className={`bg-white border-2 ${M.ring} rounded-2xl p-4 flex flex-wrap gap-3 items-start`}>
      {x.photos && x.photos[0] ? (
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${x.photos[0]}`} alt="" className="w-24 h-24 object-cover rounded-xl" />
      ) : (
        <div className="w-24 h-24 rounded-xl bg-slate-100 flex items-center justify-center text-slate-300"><Icon size={26} /></div>
      )}
      <div className="flex-1 min-w-[240px]">
        <div className="flex flex-wrap items-center gap-1.5 mb-1">
          <Badge style={{ background: M.color, color: "#fff" }} className="inline-flex items-center gap-1"><Icon size={11} />{t(`mk_type_${x.type}`)}</Badge>
          <Badge className={S.cls}>{S.icon ? <S.icon size={11} className="mr-1" /> : null}{t(S.key)}</Badge>
          <Badge variant="outline" className="text-[10px]">{labelCat(x.category)} → {labelSub(x.subcategory)} → {labelItem(x.item_type)}</Badge>
        </div>
        <Link to={x.link} data-testid={`ml-title-${x.id}`} className="font-semibold text-slate-900 hover:underline block truncate">{x.title}</Link>
        {isSale && x.price != null && <div className="text-sm text-purple-700 font-bold">€ {x.price}</div>}
        <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-3">
          <span>Pubblicato: {formatDate(x.created_at)}</span>
          {x.state === "closed" && <span data-testid={`ml-closed-at-${x.id}`}>Chiuso: {formatDate(x.closed_at)}</span>}
          {isSale && x.selected_buyer_id && (
            <span data-testid={`ml-buyer-${x.id}`} className="inline-flex items-center gap-1 text-emerald-800">
              <UserIcon size={11} /> Acquirente: <Link to={`/profile/${x.selected_buyer_id}`} className="font-semibold hover:underline">{x.selected_buyer_name || x.selected_buyer_id}</Link>
            </span>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Link to={x.link} data-testid={`ml-open-${x.id}`} className="h-9 px-3 rounded-full text-xs font-semibold bg-slate-900 text-white inline-flex items-center gap-1 hover:bg-slate-800">
          <MessageCircle size={12} /> Apri annuncio e chat
        </Link>
        {(x.type === "sale" || x.type === "gift") && x.state !== "closed" && (
          <Link to={`${x.link}?edit=1`} data-testid={`ml-edit-${x.id}`} className="h-8 px-3 rounded-full text-[11px] font-semibold bg-white border border-slate-300 text-slate-800 inline-flex items-center gap-1 hover:bg-slate-100">
            Modifica / Elimina
          </Link>
        )}
      </div>
    </div>
  );
}

function ReportRow({ x }) {
  const { t } = useI18n();
  const S = REPORT_STATE_META[x.state] || REPORT_STATE_META.published;
  const R = RISK_META[x.risk] || {};
  return (
    <div data-testid={`ml-report-${x.id}`} className="bg-white border-2 border-emerald-200 rounded-2xl p-4 flex flex-wrap gap-3 items-start">
      {x.photos && x.photos[0] ? (
        <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${x.photos[0]}`} alt="" className="w-24 h-24 object-cover rounded-xl" />
      ) : (
        <div className="w-24 h-24 rounded-xl bg-emerald-50 flex items-center justify-center text-emerald-400"><Leaf size={26} /></div>
      )}
      <div className="flex-1 min-w-[240px]">
        <div className="flex flex-wrap items-center gap-1.5 mb-1">
          <Badge style={{ background: "#059669", color: "#fff" }} className="inline-flex items-center gap-1"><Leaf size={11} />Segnalazione</Badge>
          {R.label && <Badge style={{ background: R.color, color: "#fff" }}>{R.label}</Badge>}
          <Badge className={S.cls}>{t(S.key)}</Badge>
          {x.category && <Badge variant="outline" className="text-[10px]">{x.category}{x.subcategory ? ` · ${x.subcategory}` : ""}</Badge>}
        </div>
        <Link to={x.link} className="font-semibold text-slate-900 hover:underline block line-clamp-2">{x.title}</Link>
        <div className="text-[11px] text-slate-500 mt-1 flex flex-wrap gap-3">
          <span>Inviata: {formatDate(x.created_at)}</span>
          {x.province && <span className="inline-flex items-center gap-1"><MapPin size={11} />{x.province}</span>}
        </div>
      </div>
      <div className="flex flex-col gap-1">
        <Link to={x.link} className="h-9 px-3 rounded-full text-xs font-semibold bg-slate-900 text-white inline-flex items-center gap-1 hover:bg-slate-800">
          <MessageCircle size={12} /> Apri segnalazione
        </Link>
      </div>
    </div>
  );
}
